import {
  Autorenew as AutorenewIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Pagination } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ReverseApi } from "~/functions/api/reverse";
import { useWebPubSubConnection } from "~/hooks/useWebPubSubConnection";
import ContextMenu from "~/images/context_menu.svg?react";
import EmptyStateIcon from "~/images/Group26781.svg?react";
import type {
  ReverseEngineeringReport,
  ReverseEngineeringReportStatus,
  ReverseEngineeringStatusInfo,
} from "~/interface/reverse";
import type { RootState } from "~/store";
import ReportCreationStep from "../../components/ReverseEngineering/ReportCreationStep";
import ReverseEngineeringModal from "../../components/ReverseEngineering/ReverseEngineeringModal";
import { CommonButton } from "../../components/ui/commonButton";
import CreditLackModal from "../../components/ui/creditLackModal";
import Modal from "../../components/ui/modal";
import PageHeader from "../../components/ui/pageHeader";
import styles from "./index.module.scss";

const ReverseEngineering = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReverseEngineeringReport | null>(null);
  const [showAppsPopup, setShowAppsPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [reports, setReports] = useState<ReverseEngineeringReport[]>([]);
  const [openMenuRow, setOpenMenuRow] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;
  const {
    currentProject,
    currentUser,
    organization,
    status: authStatus,
  } = useSelector((state: RootState) => state.auth);
  const [isCreditLackModalOpen, setIsCreditLackModalOpen] = useState(false);
  const [lackCredit, setLackCredit] = useState(0);
  const [processingReport, setProcessingReport] = useState<ReverseEngineeringReport | null>(null);

  useWebPubSubConnection();

  const handleGenerateReverseEngineering = async () => {
    setIsModalOpen(false);
    setTimeout(() => {
      fetchReports();
    }, 1000);
  };

  const fetchReports = useCallback(async () => {
    // 認証状態がロード中の場合は何もしない（authStatusが変わったら再実行される）
    if (authStatus === "loading") {
      return;
    }

    // ユーザー情報が取得できていない場合はローディングを解除して終了
    if (!currentUser?.organization_id) {
      setIsLoading(false);
      return;
    }

    if (!currentProject?.id || !currentProject?.organization_id) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    setIsLoading(true);
    try {
      const reverseApi = new ReverseApi();
      const fetchedReports = await reverseApi.getReverseEngineeringReports(
        currentProject.organization_id,
        currentProject.id,
        page,
        itemsPerPage,
      );
      if (fetchedReports) {
        setReports(fetchedReports.reports);
        setTotalCount(fetchedReports.totalCount);
      }
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.FETCH_REPORTS_FAILED);
    } finally {
      setIsLoading(false);
    }
  }, [
    authStatus,
    currentProject?.id,
    currentProject?.organization_id,
    currentUser?.organization_id,
    navigate,
    page,
  ]);

  const handleDeleteReport = async (reportId: string) => {
    if (!currentProject?.id || !currentUser?.id || !currentUser?.organization_id) {
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.DELETE_MISSING_INFO);
      return;
    }

    const reverseApi = new ReverseApi();
    const success = await reverseApi.deleteReverseEngineeringReport(
      currentUser.organization_id,
      currentProject.id,
      reportId,
    );

    if (success) {
      setReports(reports.filter((report) => report.id !== reportId));
      toast.success(TOAST_MESSAGES.REVERSE_ENGINEERING.DELETE_SUCCESS);
    } else {
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.DELETE_FAILED);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleNewReportClick = () => {
    // クレジットチェック (リバースエンジニアリングは50クレジット消費)
    if (organization) {
      const creditCost = 50;
      if (organization.remainingCredit < creditCost) {
        setLackCredit(creditCost - organization.remainingCredit);
        setIsCreditLackModalOpen(true);
        return;
      }
    }
    setIsModalOpen(true);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleReportClick = (report: ReverseEngineeringReport) => {
    const status = convertToDisplayStatus(report.status);
    if (status === "processing") {
      setProcessingReport(report);
    } else {
      // useNavigateで遷移（<a>タグのhrefだとフルページリロードになる）
      navigate(`${location.pathname}/report/${report.id}`);
    }
  };

  const handleProcessingComplete = () => {
    if (processingReport) {
      // 完了したら詳細ページに遷移
      navigate(`${location.pathname}/report/${processingReport.id}`);
      setProcessingReport(null);
    }
  };

  const convertToDisplayStatus = (
    statusInfo: ReverseEngineeringStatusInfo | null | undefined,
  ): ReverseEngineeringReportStatus => {
    if (!statusInfo?.runtimeStatus) {
      return "unknown";
    }

    // "OrchestrationRuntimeStatus.Completed" → "completed" のように変換
    const runtimeStatus = statusInfo.runtimeStatus
      .replace("OrchestrationRuntimeStatus.", "")
      .toLowerCase();

    switch (runtimeStatus) {
      case "completed":
        return "completed";
      case "running":
        return "processing";
      case "failed":
      case "terminated":
        return "failed";
      case "pending":
        return "pending";
      default:
        return "unknown";
    }
  };

  const getStatusBadge = (statusInfo: ReverseEngineeringStatusInfo | null | undefined) => {
    const iconClassName = styles["statusIcon"] || "";
    const statusConfig = {
      completed: {
        label: "完了",
        className: styles["completed"] || "",
        icon: <CheckCircleIcon className={iconClassName} />,
      },
      processing: {
        label: "処理中",
        className: styles["processing"] || "",
        icon: <AutorenewIcon className={`${iconClassName} ${styles["spinning"] || ""}`} />,
      },
      failed: {
        label: "失敗",
        className: styles["failed"] || "",
        icon: <ErrorIcon className={iconClassName} />,
      },
      pending: {
        label: "待機中",
        className: styles["pending"] || "",
        icon: <PendingIcon className={iconClassName} />,
      },
      unknown: {
        label: "不明",
        className: styles["unknown"] || "",
        icon: <HelpOutlineIcon className={iconClassName} />,
      },
    };

    const status = convertToDisplayStatus(statusInfo);
    const config = statusConfig[status];
    return (
      <span className={`${styles["statusBadge"] || ""} ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <div className={styles["container"]}>
      <PageHeader
        title="リバースエンジニアリング"
        titleActions={
          <button
            type="button"
            className={styles["refreshButton"]}
            onClick={() => fetchReports()}
            disabled={isLoading}
            aria-label="リストを更新"
          >
            <RefreshIcon className={isLoading ? (styles["spinning"] ?? "") : ""} />
          </button>
        }
      >
        <CommonButton onClick={handleNewReportClick}>新規レポート作成</CommonButton>
      </PageHeader>

      {isLoading ? (
        <div className={styles["loadingState"]}>
          <p>ロード中...</p>
        </div>
      ) : reports.length > 0 ? (
        <div className={styles["reportTable"]}>
          <div className={styles["reportRowHeader"]}>
            <div>レポート</div>
            <div>対象アプリ</div>
            <div>ステータス</div>
            <div>作成日</div>
            <div>作成者</div>
            <div></div>
          </div>
          {reports.map((report) => (
            // biome-ignore lint/a11y/useSemanticElements: existing code pattern for clickable rows
            <div
              key={report.id}
              className={styles["reportRow"]}
              onClick={() => handleReportClick(report)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleReportClick(report);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={styles["reportName"]}>
                {report.name ||
                  `分析レポート_${
                    typeof report.created_at === "string" || typeof report.created_at === "number"
                      ? new Date(report.created_at).toLocaleDateString("ja-JP").replace(/\//g, "")
                      : report.created_at.toLocaleDateString("ja-JP").replace(/\//g, "")
                  }`}
              </div>
              <div className={styles["targetApps"]}>
                {report.kintone_apps && report.kintone_apps.length > 0 ? (
                  <>
                    <span>
                      {report.kintone_apps
                        .slice(0, 3)
                        .map((app) => app.name)
                        .join("、")}
                    </span>
                    {report.kintone_apps.length > 3 && (
                      <>
                        <span>、</span>
                        <button
                          type="button"
                          className={styles["appsLink"]}
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setPopupPosition({
                              top: rect.bottom + window.scrollY + 5,
                              left: rect.left + window.scrollX,
                            });
                            setSelectedReport(report);
                            setShowAppsPopup(true);
                          }}
                        >
                          他{report.kintone_apps.length - 3}件
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <span>{(report.kintone_app_ids || []).slice(0, 3).join("、")}</span>
                )}
              </div>
              <div className={styles["statusCell"]}>{getStatusBadge(report.status)}</div>
              <div>
                {typeof report.created_at === "string" || typeof report.created_at === "number"
                  ? new Date(report.created_at).toLocaleDateString("ja-JP")
                  : report.created_at.toLocaleDateString("ja-JP")}
              </div>
              <div>
                {typeof report.created_by === "object" && report.created_by
                  ? report.created_by.display_name
                  : report.created_by}
              </div>
              <div className={styles["btnCell"]}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuRow(openMenuRow === report.id ? null : report.id);
                  }}
                >
                  <ContextMenu />
                </button>
                {openMenuRow === report.id && (
                  <div className={styles["menu"]}>
                    <button
                      type="button"
                      className={styles["delete"]}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteReport(report.id);
                      }}
                    >
                      削除
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles["emptyState"]}>
          <EmptyStateIcon />
          <p>リバースエンジニアリングでは、kintoneのユーザー情報を入力すると、</p>
          <p>アプリ間の依存関係、ER図、機能要件を生成します。</p>
        </div>
      )}
      {!isLoading && reports.length > 0 && totalCount > itemsPerPage && (
        <div className={styles["pagination"]}>
          <Pagination
            count={Math.ceil(totalCount / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </div>
      )}
      <ReverseEngineeringModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onComplete={handleGenerateReverseEngineering}
      />

      <CreditLackModal
        isOpen={isCreditLackModalOpen}
        onClose={() => setIsCreditLackModalOpen(false)}
        lackCredit={lackCredit}
      />

      {/* Processing Report Progress Modal */}
      {processingReport && (
        <Modal
          label="レポート作成中"
          isOpen={!!processingReport}
          onClose={() => {
            setProcessingReport(null);
            toast.info(TOAST_MESSAGES.REVERSE_ENGINEERING.BACKGROUND_PROCESSING);
            fetchReports();
          }}
          content={
            <div className={styles["processingModalContent"]}>
              <ReportCreationStep
                selectedApps={(processingReport.kintone_apps || []).map((app) => ({
                  id: app.appId,
                  appId: app.appId,
                  name: app.name,
                  description: app.description || "",
                  iconFileKey: app.iconFileKey ?? null,
                }))}
                dfInstanceId={processingReport.df_instance_id || ""}
                reportId={processingReport.id}
                organizationId={currentProject?.organization_id}
                projectId={currentProject?.id}
                initialCheckpoint={processingReport.status?.checkpoint}
                onAllTasksComplete={handleProcessingComplete}
                onCompletionAvailabilityChange={(available) => {
                  if (available) {
                    handleProcessingComplete();
                  }
                }}
                onClose={() => {
                  setProcessingReport(null);
                  toast.info(TOAST_MESSAGES.REVERSE_ENGINEERING.BACKGROUND_PROCESSING);
                  fetchReports();
                }}
              />
            </div>
          }
          className="reverseEngineeringModal"
        />
      )}

      {/* Target Apps Popup */}
      {showAppsPopup && selectedReport && (
        <>
          {/* biome-ignore lint/a11y/useSemanticElements: overlay backdrop requires div for styling */}
          <div
            className={styles["popupOverlay"]}
            onClick={() => setShowAppsPopup(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowAppsPopup(false);
            }}
            role="button"
            tabIndex={-1}
            aria-label="ポップアップを閉じる"
          />
          <div
            className={styles["appsPopup"]}
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
            }}
          >
            <div className={styles["popupHeader"]}>
              <span className={styles["popupTitle"]}>対象アプリ一覧</span>
              <button
                type="button"
                className={styles["popupClose"]}
                onClick={() => setShowAppsPopup(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles["popupContent"]}>
              {selectedReport.kintone_apps && selectedReport.kintone_apps.length > 0
                ? selectedReport.kintone_apps.map((app) => (
                    <div key={app.appId} className={styles["popupItem"]}>
                      {app.name} ({app.appId})
                    </div>
                  ))
                : (selectedReport.kintone_app_ids || []).map((appId: string) => (
                    <div key={appId} className={styles["popupItem"]}>
                      {appId}
                    </div>
                  ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReverseEngineering;
